import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Check, Sparkles, Zap,
  Heart, Swords, RefreshCw,
} from 'lucide-react';
import { api } from '../lib/api';
import { themeOf } from '../lib/classTheme';

/* ============================================================
   STEP A — pick a class
   ============================================================ */
export function ClassPicker({ value, onChange }) {
  const { data, isLoading } = useQuery({
    queryKey: ['heroClasses'],
    queryFn: () => api.get('/templates/classes').then(r => r.data),
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skeleton h-36 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
      {data?.classes?.map((c, i) => {
        const t = themeOf(c.key);
        const active = value === c.key;
        const disabled = c.available === 0;

        return (
          <motion.button
            key={c.key}
            type="button"
            disabled={disabled}
            onClick={() => onChange(c.key)}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            whileTap={{ scale: 0.95 }}
            className={`relative p-3 rounded-2xl border-2 overflow-hidden text-left transition-all
              ${active ? `${t.border} ${t.bgSoft} shadow-lg` : 'border-ink-200 bg-white hover:border-ink-300'}
              ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            style={active ? { boxShadow: t.glowCss } : {}}
          >
            <div className="absolute inset-0 transition-opacity"
              style={{ background: t.radial, opacity: active ? 0.5 : 0 }} />

            {active && (
              <motion.div layoutId="classCheck"
                className={`absolute top-2 right-2 w-5 h-5 rounded-full ${t.bg}
                  grid place-items-center text-white shadow-lg z-10`}>
                <Check size={12} strokeWidth={3} />
              </motion.div>
            )}

            <div className="relative">
              <motion.div className="text-3xl mb-1.5"
                animate={active ? { scale: [1, 1.22, 1], rotate: [0, -8, 8, 0] } : {}}
                transition={{ duration: 0.55 }}>
                {c.icon}
              </motion.div>

              <p className={`font-display font-extrabold text-sm ${active ? t.text : 'text-ink-900'}`}>
                {c.name}
              </p>
              <p className="text-[9px] font-bold text-ink-400 uppercase tracking-wide mt-0.5 truncate">
                {c.gender === 'female' ? '♀' : '♂'} {c.element}
              </p>
              <p className="text-[10px] text-ink-500 mt-1 leading-tight line-clamp-2 min-h-[26px]">
                {c.tagline}
              </p>

              <div className="flex flex-wrap gap-0.5 mt-1.5">
                {Object.entries(c.statBonus).slice(0, 3).map(([k, v]) => (
                  <span key={k}
                    className={`text-[8px] font-black px-1 py-0.5 rounded
                      ${active ? `${t.bg} text-white` : 'bg-ink-100 text-ink-500'}`}>
                    +{v}{k.replace('_', '').slice(0, 3).toUpperCase()}
                  </span>
                ))}
              </div>

              <p className="text-[8px] font-bold text-ink-400 mt-1.5">
                {disabled ? '⚠️ unavailable' : `${c.available} heroes`}
              </p>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
}

/* ============================================================
   STEP B — pick a hero variant
   ============================================================ */
export function HeroPicker({ classKey, value, onChange }) {
  const [index, setIndex] = useState(0);
  const scrollRef = useRef(null);
  const t = themeOf(classKey);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['heroVariants', classKey],
    queryFn: () => api.get(`/templates/${classKey}`).then(r => r.data),
    enabled: !!classKey,
  });

  useEffect(() => {
    if (data?.variants?.length && !value) {
      onChange(data.variants[0].id);
      setIndex(0);
    }
  }, [data, value, onChange]);

  useEffect(() => {
    if (!data?.variants) return;
    const i = data.variants.findIndex(v => v.id === value);
    if (i >= 0) setIndex(i);
  }, [value, data]);

  const go = (dir) => {
    if (!data?.variants?.length) return;
    const n = (index + dir + data.variants.length) % data.variants.length;
    setIndex(n);
    onChange(data.variants[n].id);
    scrollRef.current?.children[n]?.scrollIntoView({
      behavior: 'smooth', inline: 'center', block: 'nearest',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-2.5">
        <div className="skeleton w-full rounded-2xl" style={{ aspectRatio: '9/13' }} />
        <div className="flex gap-1.5 justify-center">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skeleton w-12 h-[68px] rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data?.variants?.length) {
    return (
      <div className="card p-6 text-center">
        <Sparkles size={30} className="mx-auto text-ink-300 mb-2" />
        <p className="font-bold text-sm text-ink-700">No heroes available yet</p>
        <p className="text-xs text-ink-400 mt-1">
          {error?.message || 'This class is being prepared. Try another one.'}
        </p>
        <button onClick={() => refetch()} className="btn-ghost btn-sm mt-3">
          <RefreshCw size={12} /> Retry
        </button>
      </div>
    );
  }

  const hero = data.variants[index];
  const d = hero.descriptor || {};

  return (
    <div className="space-y-2.5">
      {/* ─── MAIN CARD — full width, capped height ─── */}
      <div className="relative w-full rounded-2xl overflow-hidden border-2 shadow-xl"
        style={{ borderColor: t.hex, boxShadow: t.glowCss }}>

        <div className="absolute inset-0 z-0" style={{ background: t.radial }} />

        <AnimatePresence mode="wait">
          <motion.div key={hero.id}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.25 }}
            className="relative w-full bg-ink-900"
            style={{ aspectRatio: '9/13' }}>

            <img src={hero.image} alt={hero.name}
              className="absolute inset-0 w-full h-full object-cover object-top"
              draggable={false} />

            <div className="absolute inset-x-0 bottom-0 h-32
              bg-gradient-to-t from-black/90 via-black/45 to-transparent" />

            <div className="absolute inset-x-0 bottom-0 p-3 text-white">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-base">{t.icon}</span>
                <span className="text-[9px] font-black uppercase tracking-widest opacity-85">
                  {t.name} · #{hero.variant}
                </span>
              </div>
              <h3 className="font-display text-lg font-extrabold leading-tight truncate">
                {hero.name}
              </h3>
              {d.ethnicity && (
                <p className="text-[10px] opacity-80 font-semibold mt-0.5 truncate">
                  {d.ethnicity} · {d.age}y · {String(d.build || '').split(',')[0]}
                </p>
              )}
            </div>

            <div className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full
              bg-black/55 backdrop-blur text-white text-[10px] font-black">
              {index + 1}/{data.variants.length}
            </div>
          </motion.div>
        </AnimatePresence>

        <button type="button" onClick={() => go(-1)}
          className="absolute left-1.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full
            bg-black/50 backdrop-blur text-white grid place-items-center
            hover:bg-black/70 active:scale-90 transition z-20">
          <ChevronLeft size={17} />
        </button>
        <button type="button" onClick={() => go(1)}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full
            bg-black/50 backdrop-blur text-white grid place-items-center
            hover:bg-black/70 active:scale-90 transition z-20">
          <ChevronRight size={17} />
        </button>
      </div>

      {/* ─── THUMBNAILS ─── */}
      <div ref={scrollRef} className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
        {data.variants.map((v, i) => (
          <button key={v.id} type="button"
            onClick={() => { setIndex(i); onChange(v.id); }}
            className={`relative shrink-0 w-12 h-[68px] rounded-lg overflow-hidden border-2 transition-all
              ${i === index ? 'scale-105' : 'opacity-55 hover:opacity-85'}`}
            style={i === index
              ? { borderColor: t.hex, boxShadow: `0 0 12px ${t.hex}88` }
              : { borderColor: '#e2e8f0' }}>
            <img src={v.thumb || v.image} alt=""
              className="w-full h-full object-cover object-top" />
            {i === index && (
              <div className="absolute inset-x-0 bottom-0 h-0.5" style={{ background: t.hex }} />
            )}
          </button>
        ))}
      </div>

      {/* ─── STARTING STATS ─── */}
      <div className="card p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="label !mb-0">Starting stats</p>
          <div className="flex gap-1">
            <span className="chip-ink !text-[9px]"><Heart size={8} /> {data.derived.maxHp}</span>
            <span className="chip-ink !text-[9px]"><Zap size={8} /> {data.derived.power}</span>
          </div>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1">
          {Object.entries(data.startingStats).map(([k, v]) => {
            const boosted = v > 5;
            return (
              <div key={k}
                className={`px-1 py-1 rounded-lg text-center ${boosted ? t.bgSoft : 'bg-ink-50'}`}>
                <p className={`font-black text-xs ${boosted ? t.text : 'text-ink-700'}`}>{v}</p>
                <p className="text-[7px] font-bold text-ink-400 uppercase truncate">
                  {k.replace('_', '').slice(0, 4)}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <ClassMoves classKey={classKey} />
    </div>
  );
}

/* ============================================================
   MOVES PREVIEW
   ============================================================ */
function ClassMoves({ classKey }) {
  const { data } = useQuery({
    queryKey: ['heroClasses'],
    queryFn: () => api.get('/templates/classes').then(r => r.data),
  });
  const cls = data?.classes?.find(c => c.key === classKey);
  if (!cls?.moves?.length) return null;

  return (
    <div className="card p-3">
      <p className="label flex items-center gap-1"><Swords size={10} /> Combat moves</p>
      <div className="grid grid-cols-6 gap-1">
        {cls.moves.map(m => (
          <div key={m.key} className="bg-ink-50 rounded-lg p-1.5 text-center" title={m.desc}>
            <div className="text-base">{m.icon}</div>
            <p className="text-[7px] font-black leading-tight mt-0.5 truncate">
              {m.name.split(' ')[0]}
            </p>
          </div>
        ))}
      </div>
      <p className="text-[9px] text-ink-400 font-semibold mt-1.5">
        Pick your move, strike zone and guard zone each turn.
      </p>
    </div>
  );
}

/* ============================================================
   COMBINED
   ============================================================ */
export default function CharacterSelect({ classKey, templateId, onClass, onTemplate }) {
  return (
    <div className="space-y-4">
      <div>
        <p className="label">1 · Choose your class</p>
        <ClassPicker value={classKey} onChange={(k) => { onClass(k); onTemplate(null); }} />
      </div>

      <AnimatePresence>
        {classKey && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <p className="label">2 · Choose your hero</p>
            <HeroPicker classKey={classKey} value={templateId} onChange={onTemplate} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}